'use client';

import React, { memo, useMemo } from 'react';
import { Spinner } from 'components/Spinner';
import styles from '../../Collections.module.css';
import type { ListProps } from '../types';

type Props<T> = Pick<
  ListProps<T>,
  'loading' | 'renderLoading' | 'renderPlaceholder'
> & {
  collectionSize?: number;
  query?: string;
};

function ListBoxLoading<T>(props: Props<T>) {
  const content = useMemo(() => {
    if (props.loading) {
      // allow consumer to customize the loading state
      const node = props.renderLoading?.(props.loading);

      // if a consumer provides a component, use that
      if (node) {
        return node;
      }

      // fallback to default loading components
      switch (props.loading) {
        case 'loading':
        case 'loading-more':
          return <Spinner aria-label="Loading" centered delay={0} isLoading />;
        case 'loading-error':
        case 'loading-more-error':
        default:
          return null;
      }
    }

    if (props.collectionSize === 0 && props.renderPlaceholder) {
      // @ts-expect-error FIXME(strictNullChecks)
      return props.renderPlaceholder(props.query);
    }

    return null;
  }, [props]);

  if (content) {
    return props.collectionSize === 0 ? (
      <div className={styles.loadingFill}>{content}</div>
    ) : (
      <div className={styles.loadingFooter}>{content}</div>
    );
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{null}</>;
}

const _ListBoxLoading = memo(ListBoxLoading);
_ListBoxLoading.displayName = 'ListBoxLoading';
export { _ListBoxLoading as ListBoxLoading };
